import { Type } from '@angular/core';
import { InputBuscarComponent } from '../../../../shared/components/input-buscar/input-buscar.component';
import { AcessoRapidoBannerComponent } from '../acesso-rapido-banner/acesso-rapido-banner.component';
import { BannerComponent } from '../banner/banner.component';
import { CategoriasServicosComponent } from '../categorias-servicos/categorias-servicos.component';
import { LinksAcessoRapidoComponent } from '../links-acesso-rapido/links-acesso-rapido.component';
import { SectionNoticiasComponent } from '../section-noticias/section-noticias.component';
import { ServicosMaisProcuradosComponent } from '../servicos-mais-procurados/servicos-mais-procurados.component';

/**
 * Identificador de cada seção ordenável da homepage. O back-end grava outra chave para o
 * mesmo bloco (ver `secaoDaChaveApi`) — o cabeçalho e o rodapé não entram aqui porque são
 * fixos no layout.
 */
export type SecaoHomepageId =
  | 'busca'
  | 'banner'
  | 'acesso-rapido'
  | 'servicos-mais-procurados'
  | 'categorias-servicos'
  | 'noticias'
  | 'links-acesso-rapido';

/** Uma seção do catálogo: o id do contrato, os rótulos do adm e o componente renderizado. */
export interface SecaoHomepage {
  readonly id: SecaoHomepageId;
  /** Rótulo exibido na tela de ordenação da área administrativa. */
  readonly titulo: string;
  readonly descricao: string;
  readonly componente: Type<unknown>;
}

/**
 * Um bloco da homepage como a API devolve em `GET publico/padrao-homepage`: a chave do adm,
 * a posição (1 é o primeiro) e se o órgão o publica. Os ocultos também vêm.
 */
export interface BlocoHomepagePublico {
  desChaveBlh: string;
  numOrdemBlh: number;
  bitVisivelBlh: boolean;
}

/**
 * Chave do bloco na API (a que o adm grava, com CHECK no banco) → seção do portal.
 * Os nomes diferem por história: a API guarda o nome do bloco no adm, o portal o do componente.
 */
const SECAO_POR_CHAVE_API: Readonly<Record<string, SecaoHomepageId>> = {
  busca: 'busca',
  banner: 'banner',
  acessoRapidoBanner: 'acesso-rapido',
  servicosMaisProcurados: 'servicos-mais-procurados',
  categoriasServicos: 'categorias-servicos',
  noticias: 'noticias',
  acessoRapido: 'links-acesso-rapido',
};

/** A seção do portal de uma chave da API, ou `null` para chave que o portal não conhece. */
export function secaoDaChaveApi(chave: string): SecaoHomepageId | null {
  return Object.hasOwn(SECAO_POR_CHAVE_API, chave) ? SECAO_POR_CHAVE_API[chave] : null;
}

/**
 * Catálogo das seções na ordem padrão — é o que a homepage exibe enquanto o
 * back-end não devolve uma ordenação salva.
 */
export const SECOES_HOMEPAGE: readonly SecaoHomepage[] = [
  {
    id: 'busca',
    titulo: 'Busca de serviços',
    descricao: 'Campo de busca por serviço digital, agendamento, presencial ou ouvidoria.',
    componente: InputBuscarComponent,
  },
  {
    id: 'banner',
    titulo: 'Banner principal',
    descricao: 'Carrossel de banners com as redes sociais do órgão.',
    componente: BannerComponent,
  },
  {
    id: 'acesso-rapido',
    titulo: 'Acesso rápido',
    descricao: 'Atalhos em destaque para serviços, agendamento, ouvidoria e SIC.',
    componente: AcessoRapidoBannerComponent,
  },
  {
    id: 'servicos-mais-procurados',
    titulo: 'Serviços mais procurados',
    descricao: 'Os serviços mais acessados no portal nos últimos 30 dias, completados pelo catálogo.',
    componente: ServicosMaisProcuradosComponent,
  },
  {
    id: 'categorias-servicos',
    titulo: 'Categorias de serviços',
    descricao: 'As oito primeiras categorias publicadas, pela ordem de exibição do cadastro.',
    componente: CategoriasServicosComponent,
  },
  {
    id: 'noticias',
    titulo: 'Notícias',
    descricao: 'Listagem de notícias com filtro por categoria e paginação.',
    componente: SectionNoticiasComponent,
  },
  {
    id: 'links-acesso-rapido',
    titulo: 'Links de acesso rápido',
    descricao: 'Política de atendimento, canais de atendimento, LGPD e links dinâmicos.',
    componente: LinksAcessoRapidoComponent,
  },
];

/** Ordem exibida quando não há configuração salva. */
export const ORDEM_PADRAO_HOMEPAGE: readonly SecaoHomepageId[] = SECOES_HOMEPAGE.map(
  (secao) => secao.id
);

/** Descarta ids que o front não conhece (seção removida ou back-end à frente do deploy). */
export function ehSecaoHomepageId(valor: string): valor is SecaoHomepageId {
  return SECOES_HOMEPAGE.some((secao) => secao.id === valor);
}

/**
 * As seções que a homepage exibe a partir dos blocos do órgão: na ordem dele, sem as que ele
 * ocultou. Chave desconhecida é ignorada, e a seção que os blocos não citam entra no fim, na
 * ordem padrão — a mesma regra de `ordenarSecoesHomepage`. Sem blocos, vale a ordem padrão.
 */
export function secoesDosBlocos(blocos: readonly BlocoHomepagePublico[]): readonly SecaoHomepage[] {
  const ordenados = [...blocos].sort((a, b) => a.numOrdemBlh - b.numOrdemBlh);
  const ocultas = new Set<SecaoHomepageId>();
  const ordem: SecaoHomepageId[] = [];

  for (const bloco of ordenados) {
    const secao = secaoDaChaveApi(bloco.desChaveBlh);
    if (!secao) continue;

    if (bloco.bitVisivelBlh) ordem.push(secao);
    else ocultas.add(secao);
  }

  return ordenarSecoesHomepage(ordem).filter((secao) => !ocultas.has(secao.id));
}

/**
 * Resolve a ordem informada contra o catálogo: remove ids repetidos ou desconhecidos e
 * acrescenta ao fim, na ordem padrão, as seções que a configuração não citou — assim uma
 * seção nova nunca some da homepage por causa de uma ordenação antiga.
 */
export function ordenarSecoesHomepage(
  ordem: readonly SecaoHomepageId[]
): readonly SecaoHomepage[] {
  const porId = new Map(SECOES_HOMEPAGE.map((secao) => [secao.id, secao]));
  const ordenadas: SecaoHomepage[] = [];

  for (const id of ordem) {
    const secao = porId.get(id);
    if (!secao) continue;
    porId.delete(id);
    ordenadas.push(secao);
  }

  return [...ordenadas, ...porId.values()];
}
